
// WHY: sonner 2.0.x замораживает отсчёт по interacting/document.hidden и в WebView не размораживает (pointercancel без
// pointerup) — часы наши
type TimeoutHandle = ReturnType<typeof setTimeout>;

export const MAX_HOLD_MS = 4000;

interface Entry {
  dismiss: (id: string) => void;
  remaining: number;
  startedAt: number;
  timer: TimeoutHandle | null;
  ceiling: TimeoutHandle;
}

const entries = new Map<string, Entry>();

let held = false;

let counter = 0;

// WHY: toast.custom с id: undefined возвращает один счётчик, а создаёт тост со следующим — возвращённый id ничего не
// закрывает
export const nextToastId = () => `toast-${++counter}`;

const drop = (id: string) => {
  const entry = entries.get(id);
  if (entry === undefined) return undefined;
  if (entry.timer !== null) clearTimeout(entry.timer);
  clearTimeout(entry.ceiling);
  entries.delete(id);
  return entry;
};

const fire = (id: string) => {
  const entry = drop(id);
  entry?.dismiss(id);
};

const run = (id: string, entry: Entry) => {
  entry.startedAt = Date.now();
  entry.timer = setTimeout(() => fire(id), entry.remaining);
};

export const scheduleAutoDismiss = (id: string, duration: number, dismiss: (id: string) => void) => {
  cancelAutoDismiss(id);
  if (!Number.isFinite(duration)) return;
  listen();
  const entry: Entry = {
    dismiss,
    remaining: duration,
    startedAt: Date.now(),
    timer: null,
    ceiling: setTimeout(() => fire(id), duration + MAX_HOLD_MS),
  };
  entries.set(id, entry);
  if (!held) run(id, entry);
};

export const cancelAutoDismiss = (id: string) => {
  drop(id);
};

export const pauseAutoDismiss = () => {
  if (held) return;
  held = true;
  for (const entry of entries.values()) {
    if (entry.timer === null) continue;
    clearTimeout(entry.timer);
    entry.timer = null;
    entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
  }
};

export const resumeAutoDismiss = () => {
  if (!held) return;
  held = false;
  for (const [id, entry] of entries) run(id, entry);
};

const overToast = (target: EventTarget | null) => target instanceof Element && target.closest('[data-sonner-toast]') !== null;

let listening = false;

const listen = () => {
  if (listening || typeof document === 'undefined') return;
  listening = true;

  document.addEventListener('pointerdown', (event) => { if (overToast(event.target)) pauseAutoDismiss(); }, true);

  document.addEventListener('pointerup', resumeAutoDismiss, true);
  document.addEventListener('pointercancel', resumeAutoDismiss, true);
  document.addEventListener('lostpointercapture', resumeAutoDismiss, true);

  // WHY: hover-hold только для настоящей мыши — тап шлёт совместимые mouse-события, а mouseout может не прийти
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'mouse' && overToast(event.target)) pauseAutoDismiss();
  }, true);
  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || overToast(event.relatedTarget)) return;
    resumeAutoDismiss();
  }, true);
};
